import React, { useState } from 'react';
import { useCart } from 'react-use-cart';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../Contexts/NotificationContext';
import '../../../Assets/Stylesheets/Pages/MarketingServices/OptionPrebuiltPaid.scss';
import API_BASE_URL from '../../../config/api';
import { GlowingEffect } from '../../../Shared/GlowingEffect/GlowingEffect';

// Import all the platform logos
import googleLogo from '../../../Assets/Images/Google_logo.png';
import youtubeLogo from '../../../Assets/Images/Youtube_logo.png';
import facebookLogo from '../../../Assets/Images/Facebook_logo.png';
import instagramLogo from '../../../Assets/Images/Instagram_logo.png';
import tiktokLogo from '../../../Assets/Images/Tiktok_logo.png';
import xLogo from '../../../Assets/Images/X_logo.png';
import linkedinLogo from '../../../Assets/Images/Linkedin_logo.png';
import pinterestLogo from '../../../Assets/Images/Pinterest_logo.png';
import spotifyLogo from '../../../Assets/Images/Spotify_logo.png';
import snapchatLogo from '../../../Assets/Images/Snapchat_logo.png';
import amazonLogo from '../../../Assets/Images/Amazon_logo.png';
import moreLogo from '../../../Assets/Images/More_logo.png';

// Array to hold the platforms' data
const platforms = [
  { id: 'google', name: 'Google', logo: googleLogo },
  { id: 'youtube', name: 'YouTube', logo: youtubeLogo },
  { id: 'facebook', name: 'Facebook', logo: facebookLogo },
  { id: 'instagram', name: 'Instagram', logo: instagramLogo },
  { id: 'tiktok', name: 'TikTok', logo: tiktokLogo },
  { id: 'x', name: 'X', logo: xLogo },
  { id: 'linkedin', name: 'LinkedIn', logo: linkedinLogo },
  { id: 'pinterest', name: 'Pinterest', logo: pinterestLogo },
  { id: 'spotify', name: 'Spotify', logo: spotifyLogo },
  { id: 'snapchat', name: 'Snapchat', logo: snapchatLogo },
  { id: 'amazon', name: 'Amazon', logo: amazonLogo },
  { id: 'more', name: 'More', logo: moreLogo },
];

// For the plans
const platformLogos = {
  google: googleLogo,
  youtube: youtubeLogo,
  facebook: facebookLogo,
  instagram: instagramLogo,
  tiktok: tiktokLogo,
  x: xLogo,
  linkedin: linkedinLogo,
  pinterest: pinterestLogo,
  spotify: spotifyLogo,
  snapchat: snapchatLogo,
  amazon: amazonLogo,
  more: moreLogo,
};

const prebuiltPlans = {
  introductory: 1499.99,
  intermediate: 2999.99,
  advanced: 4999.99,
};

export default function OptionPrebuiltPaid() {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedPlanPrice, setSelectedPlanPrice] = useState(0);
  const [mediaPlanningFees, setMediaPlanningFees] = useState(0);
  const [isPlatformCollapsed, setIsPlatformCollapsed] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  
  const { addItem } = useCart();
  const navigate = useNavigate();
  const { showSuccess, showInfo } = useNotification();

  const togglePlatformCollapse = () => {
    setIsPlatformCollapsed(!isPlatformCollapsed);
  };

  const handlePlatformSelection = (platformId) => {
    if (platformId !== 'more') {
      setSelectedPlatform(platformId);
      setSelectedPlanPrice(0);
      setMediaPlanningFees(0);
      setSelectedPlan(""); // Reset the selected plan
      setIsPlatformCollapsed(true); // Auto-collapse after selection
    } else {
      showInfo("More platforms coming soon!");
      setSelectedPlatform(''); // Clear selection for "More"
    }
  };

  const formatCurrency = (number) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number);
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlanPrice(prebuiltPlans[plan]);
    setSelectedPlan(plan);
    fetchMediaPlanningFees(prebuiltPlans[plan]);
    setIsPlatformCollapsed(true); // Collapse the platform selection
  };

  const fetchMediaPlanningFees = async (budget) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-media-planning-fees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ budget }),
      });
      const data = await response.json();
      setMediaPlanningFees(data.mediaPlanningFees);
    } catch (error) {
      console.error('Error fetching media planning fees:', error);
      // Fallback in case the API call fails
      setMediaPlanningFees(budget * 0.15); // 15% of budget as fallback
    }
  };

  const subTotal = selectedPlanPrice + mediaPlanningFees;

  const handleAddToCart = () => {
    const item = {
      id: `paid-ads-${selectedPlatform}-${selectedPlan}-${Date.now()}`,
      name: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan - Paid Ads`,
      platform: selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1),
      price: subTotal,
      image: platformLogos[selectedPlatform],
      description: `Ad Budget: ${formatCurrency(selectedPlanPrice)} + Media Planning: ${formatCurrency(mediaPlanningFees)}`,
      type: 'paid-ads'
    };
    addItem(item);
    showSuccess('Item added to cart!');
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  // Custom platform component that handles the click directly
  const PlatformSelector = ({ platform }) => {
    const isSelected = selectedPlatform === platform.id;
    
    return (
      <div 
        className={`platform-item ${isSelected ? 'selected-platform' : ''}`}
        onClick={() => handlePlatformSelection(platform.id)}
      >
        <div className="platform-wrapper">
          <div className="platform-container">
            {/* Black circular background is applied via CSS ::before */}
            {isSelected ? (
              <div className="static-glow"></div>
            ) : (
              <GlowingEffect
                spread={15}
                glow={true}
                disabled={false}
                proximity={60}
                inactiveZone={0}
                borderWidth={0}
                className="centered-glow"
              />
            )}
            <img 
              src={platform.logo} 
              alt={`${platform.name} Logo`} 
              className="platform-logo" 
            />
          </div>
          <p className={isSelected ? 'selected-name' : ''}>{platform.name}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="papo-option-prebuilt-container">
      <button onClick={togglePlatformCollapse} className="collapse-toggle">
        {isPlatformCollapsed ? 'Change Selection' : 'Hide Platform Selection'}
      </button>
      
      {!isPlatformCollapsed && (
        <div className="papo-platform-grid-container">
          {platforms.map(platform => (
            <PlatformSelector key={platform.id} platform={platform} />
          ))}
        </div>
      )}

      <hr className="section-divider" />

      {selectedPlatform && selectedPlatform !== 'more' && (
        <>
          <div className="plan-section-container">
            <h2 className="plan-section-title">Plan Selection</h2>
            <p className="plan-section-description">
              Select the plan that best fits your marketing needs. Each plan includes different levels of service and features.
            </p>
          </div>
          
          <div className="papo-prebuilt-plan-container">
            {Object.entries(prebuiltPlans).map(([plan, price]) => (
              <div 
                key={plan} 
                className={`papo-plan-card ${selectedPlan === plan ? "selected" : ""}`}
                onClick={() => handlePlanSelection(plan)}
              >
                <div className="plan-card-header">
                  <div className="platform-logo-wrapper">
                    <img src={platformLogos[selectedPlatform]} alt={`${selectedPlatform} logo`} className="papo-platform-logo" />
                  </div>
                  <h3 className="plan-title">{`${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`}</h3>
                </div>
                
                <div className="plan-price-section">
                  <span className="price-label">Starting at</span>
                  <div className="price-amount">{formatCurrency(price)}</div>
                </div>

                <div className="plan-features-list">
                  <div className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>Targeted Ads</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>Monthly Call Review</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>Analytic Dashboard</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>Monthly Optimization</span>
                  </div>
                </div>

                <button 
                  className="select-plan-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlanSelection(plan);
                  }}
                >
                  {selectedPlan === plan ? 'Selected' : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedPlanPrice > 0 && (
        <div className="papo-budget-summary-container">
          <div className="total-budget-container">
            <span className="budget-label">Paid Advertisement Budget:</span>
            <span className="budget-value">{formatCurrency(selectedPlanPrice)}</span>
          </div>
          <div className="media-planning-fees-container">
            <span className="budget-label">Media Planning Fees:</span>
            <span className="budget-value">{formatCurrency(mediaPlanningFees)}</span>
          </div>
          <div className="subtotal-container">
            <span className="budget-label">Sub-Total:</span>
            <span className="budget-value">{formatCurrency(subTotal)}</span>
          </div>
          <div className="pads-action-buttons-container">
            <button className="pads-add-to-cart" onClick={handleAddToCart}>Add to Cart</button>
            <button className="pads-buy-now" onClick={handleBuyNow}>Buy Now</button>
          </div>
        </div>
      )}
    </div>
  );
}
